import { useTranslation } from 'react-i18next';
// Authentication Hook - FIXED: Multi-company support with better refresh handling + superadmin role
import { useState, useEffect, useContext, createContext } from 'react';
import authService from '../services/authService';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 🔥 FIXED: Initialize auth state with better error handling
  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('🔧 Initializing auth...');

        if (authService.isAuthenticated()) {
          // Get stored data first
          const userData = authService.getUser();
          const companiesData = authService.getCompanies();
          const currentCompanyData = authService.getCurrentCompany();

          console.log('📦 Found stored data:', {
            user: !!userData,
            companies: companiesData?.length || 0,
            currentCompany: !!currentCompanyData
          });

          // Set initial state from stored data
          setUser(userData);
          setCompanies(companiesData || []);
          setCurrentCompany(currentCompanyData);
          setIsAuthenticated(true);

          // 🔥 FIXED: Always try to refresh, but don't fail if it doesn't work
          try {
            const result = await authService.refreshUser();
            if (result.success && result.data) {
              console.log('✅ Refreshed user data from server');
              setUser(result.data.user);
              setCompanies(result.data.companies || []);

              // Keep current company if still valid, otherwise use first available
              const validCompany = (result.data.companies || []).find(
                c => c.company_id === currentCompanyData?.company_id
              );
              const newCurrentCompany = validCompany || result.data.companies?.[0] || null;
              setCurrentCompany(newCurrentCompany);

              // Update stored current company if changed
              if (newCurrentCompany && newCurrentCompany.company_id !== currentCompanyData?.company_id) {
                authService.setCurrentCompany(newCurrentCompany);
              }
            } else {
              console.warn('⚠️ Could not refresh user data, using stored data');
            }
          } catch (refreshError) {
            console.warn('⚠️ Refresh failed, using stored data:', refreshError);
          }
        } else {
          console.log('❌ User not authenticated');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error(t('hardcoded.hooks_useauth.auth_initialization_error'), error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          authService.logout();
        }
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
        console.log(t('hardcoded.hooks_useauth.auth_initialization_complete'));
      }
    };

    initAuth();

    // Setup auth listener for changes in other tabs
    authService.setupAuthListener((authenticated) => {
      console.log('🔄 Auth state changed:', authenticated);
      setIsAuthenticated(authenticated);
      if (!authenticated) {
        setUser(null);
        setCompanies([]);
        setCurrentCompany(null);
      }
    });
  }, []);

  // Login function with company selection
  const login = async (email, password, companyId = null) => {
    try {
      const result = await authService.login(email, password, companyId);

      if (result.success) {
        setUser(result.data.user);
        setCompanies(result.data.companies || []);
        setCurrentCompany(result.data.current_company);
        setIsAuthenticated(true);

        return {
          success: true,
          requiresCompanySelection: result.data.requires_company_selection,
          companies: result.data.companies,
          currentCompany: result.data.current_company
        };
      }

      return { success: false, message: t('hooks_useauth.login_failed') };
    } catch (error) {
      return { success: false, message: error?.response?.data?.detail || t('hooks_useauth.login_failed') };
    }
  };

  // Switch company function
  const switchCompany = async (companyId) => {
    try {
      const result = await authService.switchCompany(companyId);

      if (result.success) {
        setUser(result.data.user);
        setCompanies(result.data.companies || []);

        const newCurrentCompany = (result.data.companies || []).find(
          c => c.company_id === companyId
        );
        setCurrentCompany(newCurrentCompany);

        return { success: true };
      }

      return { success: false, message: result.message };
    } catch (error) {
      console.error('[auth] switchCompany fallo:', error);
      return { success: false, message: t('hooks_useauth.failed_switch_company') };
    }
  };

  // Register company function
  const registerCompany = async (companyData, adminData, servicePlanId, billingPlanId = null, registrationToken = null) => {
    try {
      console.log('🔧 useAuth registerCompany called with:', { companyData, adminData, servicePlanId, billingPlanId, registrationToken });

      const result = await authService.registerCompany(companyData, adminData, servicePlanId, billingPlanId, registrationToken);

      if (result.success) {
        if (result.data?.access_token) {
          setUser(result.data.user);
          setCompanies(result.data.companies || []);
          setCurrentCompany(result.data.current_company || result.data.companies?.[0] || null);
          setIsAuthenticated(true);
        }

        return {
          success: true,
          data: result.data,
          requiresLogin: Boolean(result.data?.requires_login),
          existingUser: Boolean(result.data?.existing_user),
          message: result.data?.message,
        };
      }

      return { success: false, message: result.message };
    } catch (error) {
      console.error(t('hardcoded.hooks_useauth.useauth_registercompany_error'), error);
      return { success: false, message: t('hardcoded.hooks_useauth.registration_failed') };
    }
  };

  // Join company via invitation
  const joinCompany = async (invitationCode, userData = null) => {
    try {
      console.log('🔧 useAuth joinCompany called with:', { invitationCode, userData });

      const result = await authService.useInvitation(invitationCode, userData);

      if (result.success) {
        setUser(result.data.user);
        setCompanies(result.data.companies || []);

        const newCompany = (result.data.companies || []).find(
          c => c.company_id === result.data.user.company_id
        );
        setCurrentCompany(newCompany);
        setIsAuthenticated(true);

        return { success: true };
      }

      return { success: false, message: result.message };
    } catch (error) {
      console.error(t('hardcoded.hooks_useauth.useauth_joincompany_error'), error);
      return { success: false, message: t('hooks_useauth.failed_join_company') };
    }
  };

  // Create invitation function
  const createInvitation = async (invitationData) => {
    try {
      const result = await authService.createSmartInvitation(invitationData);
      return result;
    } catch (error) {
      console.error('[auth] createSmartInvitation fallo:', error);
      return { success: false, message: t('hooks_useauth.failed_create_invitation') };
    }
  };

  // Logout function
  const logout = () => {
    // Desuscribir Web Push de este navegador antes de cerrar sesión
    import('../services/pushClient')
      .then((m) => m.removePushSubscription())
      .catch(() => {});
    // Detener sonidos/tonos y limpiar preferencias cacheadas del chat
    import('../services/soundManager').then((m) => m.default.reset()).catch(() => {});
    import('../services/chatPrefs').then((m) => m.default.reset()).catch(() => {});
    authService.logout();
    setUser(null);
    setCompanies([]);
    setCurrentCompany(null);
    setIsAuthenticated(false);
  };

  // Update user function
  const updateUser = (userData) => {
    setUser(userData);
    authService.updateStoredUser(userData);
  };

  // 🔥 FIXED: Check user permissions for current company with superadmin support
  const hasPermission = (permission) => {
    if (!user || !currentCompany) {
      console.warn('⚠️ hasPermission called without user or currentCompany');
      return false;
    }

    const role = currentCompany.role;

    switch (permission) {
      case 'admin':
        return role === 'admin' || role === 'superadmin';
      case 'manager':
        return role === 'admin' || role === 'superadmin' || role === 'manager';
      case 'user':
        return role === 'admin' || role === 'superadmin' || role === 'manager' || role === 'user';
      default:
        return false;
    }
  };

  // Use invitation
  const useInvitation = async (invitationCode, userData = null) => {
    // OJO: aqui NO puede ir `useTranslation()`. Esta funcion se invoca desde un
    // manejador de evento (submit del registro), no durante el render, asi que
    // llamar a un hook lanzaba "Invalid hook call" ANTES del try/catch. El error
    // subia hasta el formulario como "Error inesperado" y la peticion de alta
    // nunca llegaba a salir. La `t` del AuthProvider ya esta en alcance.
    try {
      console.log('🔧 useAuth useInvitation called with:', { invitationCode, userData });

      const result = await authService.useInvitation(invitationCode, userData);

      if (result.success) {
        setUser(result.data.user);
        setCompanies(result.data.companies || []);

        const newCompany = (result.data.companies || []).find(
          c => c.company_id === result.data.user.company_id
        );
        setCurrentCompany(newCompany);
        setIsAuthenticated(true);

        return { success: true };
      }

      return { success: false, message: result.message };
    } catch (error) {
      console.error(t('hardcoded.hooks_useauth.useauth_useinvitation_error'), error);
      return { success: false, message: t('hooks_useauth.failed_use_invitation') };
    }
  };

  // Get user's role in current company
  const getCurrentRole = () => {
    return currentCompany?.role || 'user';
  };

  // Check if user has access to specific company
  const hasAccessToCompany = (companyId) => {
    return companies.some(c => c.company_id === companyId);
  };

  // Get company by ID
  const getCompanyById = (companyId) => {
    return companies.find(c => c.company_id === companyId);
  };

  const contextValue = {
    // User state
    user,
    companies,
    currentCompany,
    loading,
    isAuthenticated,

    // Auth functions
    login,
    logout,
    registerCompany,
    useInvitation,
    joinCompany,
    switchCompany,
    createInvitation,
    updateUser,

    // Permission functions
    hasPermission,
    getCurrentRole,
    hasAccessToCompany,
    getCompanyById,

    // Convenience functions
    isSuperadmin: () => getCurrentRole() === 'superadmin',
    isAdmin: () => hasPermission('admin'),
    isManager: () => hasPermission('manager'),
    isManagerOrAdmin: () => hasPermission('manager'),
    isAdminOrSuperadmin: () => getCurrentRole() === 'admin' || getCurrentRole() === 'superadmin',

    // Company info
    currentCompanyId: currentCompany?.company_id,
    currentCompanyName: currentCompany?.company_name,
    totalCompanies: companies.length,
    hasMultipleCompanies: companies.length > 1,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// useAuth Hook
export const useAuth = () => {
  const { t } = useTranslation();
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

// Higher-order component for protected routes
export const withAuth = (Component, requiredPermission = null) => {
  return function AuthenticatedComponent(props) {
  const { t } = useTranslation();
    const { isAuthenticated, loading, hasPermission } = useAuth();

    if (loading) {
      return <div>{t('hooks_useauth.loading')}</div>;
    }

    if (!isAuthenticated) {
      window.location.href = '/login';
      return null;
    }

    if (requiredPermission && !hasPermission(requiredPermission)) {
      return <div>{t('hooks_useauth.access_denied')}</div>;
    }

    return <Component {...props} />;
  };
};

export default useAuth;